import React, { useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { DataTableRowActions } from "./data-table-row-actions";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import {
  displayDate,
  displayDateTime,
  displayValue,
  formatDate,
} from "@/lib/helper";
import TableFilters from "@/components/ui/table/data-table-filter";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { DatePickerWithRange } from "@/components/ui/date-range/date-rangePicker";
import { Button } from "@/components/ui/button";
import { useGetManualCheckInsQuery } from "@/services/checkInApis";
import { CheckIn, ManualCheckIn } from "@/types/check-in";
import { Badge } from "@/components/ui/badge";
import ManualCheckInForm from "../modal/form";
import { useGetAllHardwareQuery } from "@/services/hardwareApi";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  role_id?: string[];
  sort_key: string;
  status?: boolean;
  search_key?: string;
  start_date?: string;
  end_date?: string;
  type: string;
}

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "created_at",
  type: "manual_checkin",
};
export default function CheckInLogsTable() {
  const checkInLogs = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string)
          .check_in_logs ?? "no_access"
      );
    } catch {
      return "no_access";
    }
  })();
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  const {
    searchCriteria,
    query,
    toggleSortOrder,
    setSearchCriteria,
    inputValue,
    setInputValue,
  } = useTableData(initialValue);
  const [isOpen, setOpen] = useState(false);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [data, setData] = useState<ManualCheckIn | undefined>(undefined);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any>>({});
  const [selectedUserType, setSelectedUserType] = useState<string|undefined>(undefined); // Add this state

  const {
    data: manualCheckTableData,
    refetch,
    isLoading,
  } = useGetManualCheckInsQuery({ query: query }, { skip: query == "" });

  const {
    data: hardwareData,
    isLoading: ishardwaredataloading,
    error,
    isError,
  } = useGetAllHardwareQuery({
    org_id: orgId,
    query: "",
  });

  const manualCheckTable = useMemo(() => {
    return Array.isArray(manualCheckTableData?.data)
      ? manualCheckTableData?.data
      : [];
  }, [manualCheckTableData]);

  const hardwareOptions = useMemo(() => {
    return Array.isArray(hardwareData?.data)
      ? hardwareData?.data.map((hardware) => {
          return {
            id: hardware.connection_key,
            name: hardware.name,
          };
        })
      : [];
  }, [hardwareData]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const columns: ColumnDef<CheckIn>[] = [
    {
      id: "user_name",
      meta: "user_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Name"
          sortKey="user_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 max-w-[150px]">
            <div className="w-full">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <p className="capitalize cursor-pointer text-nowrap">
                        {displayValue(row.original.user_name)}
                      </p>
                      <p className="capitalize text-xs text-gray-500">
                        {row.original.user_type}
                      </p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="capitalize">
                      {displayValue(row.original.user_name)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      },
    },

    {
      id: "staff_name",
      meta: "staff_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Staff Name"
          sortKey="staff_name"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="capitalize flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row.original.staff_name)}
          </div>
        );
      },
    },
    {
      id: "hardware_name",
      meta: "hardware_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Hardware Name"
          sortKey="hardware_name"
          sortable={true}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <div className="">
              <p className="capitalize cursor-pointer">
                {displayValue(row.original.hardware_name)}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      meta: "Status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          sortable={true}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const value = row.original?.status;
        return (
          <Badge
            className={`${value == "successfull" ? "bg-green-300 text-green-900" : "bg-red-300 text-red-900"}`}
          >
            {value}
          </Badge>
        );
      },
    },
    {
      accessorKey: "is_manual",
      meta: "is_manual",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Type"
          sortKey="is_manual"
          sortable={true}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div
            className={`${row.original.is_manual ? "text-red-900 w-full h-full bg-red-100" : ""}`}
          >
            <span>
              {row.original.is_manual ? "Manual" : "Customer Check-In"}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      meta: "created_at",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Date"
          sortKey="created_at"
          sortable={true}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <span className="flex gap-2 items-center">
            <span>{displayDateTime(row.original.created_at)}</span>
          </span>
        );
      },
    },
  ];

  const table = useReactTable({
    data: manualCheckTable,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  function handleFilterChange(field: string, value: string | number) {
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  const filterDisplay = [
    {
      type: "select",
      name: "is_manual",
      label: "Check-In Type",
      options: [
        { id: "true", name: "Manual" },
        { id: "false", name: "Customer Check-In" },
      ],
      function: (value: string) => handleFilterChange("is_manual", value),
    },
    {
      type: "select",
      name: "hardware_key",
      label: "Hardware",
      options: hardwareOptions,
      function: (value: string) => handleFilterChange("hardware_key", value),
    },
  ];

  const totalRecords = manualCheckTableData?.filtered_counts || 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });
  const handleDateRange = (dates: {
    start_date: Date | undefined;
    end_date: Date | undefined;
  }) => {
    const formattedStartDate = dates.start_date
      ? formatDate(dates.start_date)
      : "";
    const formattedEndDate = dates.end_date ? formatDate(dates.end_date) : "";

    setFilterData((prev) => ({
      ...prev,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    }));

    setSearchCriteria((prev: any) => ({
      ...prev,
      start_date: formattedStartDate ?? undefined,
      // Only set end_date if start_date is present
      end_date:
        formattedStartDate && !formattedEndDate ? undefined : formattedEndDate,
      offset: 0,
      sort_key: "created_at",
      sort_order: "desc",
    }));
  };

  

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-3 ">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center justify-center gap-2">
            <Select
              value={selectedUserType || ""}
              onValueChange={(value) => {
                setSelectedUserType(value);
                setSearchCriteria(prev => ({
                  ...prev,
                  user_type: value 
                }));
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select user type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="coach">Coach</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
              </SelectContent>
            </Select>
            <TableSearchFilter
              disable={!selectedUserType}
              inputValue={inputValue}
              initialValue={initialValue}
              setInputValue={setInputValue}
              setSearchCriteria={setSearchCriteria}
            />
          </div>
        </div>

        {/* Buttons Container */}
        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          {/* <DataTableViewOptions table={table} action={handleExportSelected} /> */}
          <div className="text-sm  text-black flex items-center gap-1  lg:mb-0 h-8">
            <DatePickerWithRange
              name={"Date Range"}
              value={{
                start_date: filterData.start_date, // Access start_date directly
                end_date: filterData.end_date, // Access end_date directly
              }}
              onValueChange={(dates) => {
                handleDateRange(dates);
              }}
              label={"Select date range"}
              className="w-full" // Ensure full width
            />
          </div>
          <Button
            variant={"outline"}
            className="text-sm  text-black flex items-center gap-1  lg:mb-0 h-8 px-2"
            onClick={() => {
              setFilterData({});
              setSearchCriteria(initialValue);
              setInputValue("");
            }}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="gap-2 flex justify-center items-center">
                    <i className="fa-solid fa-arrows-rotate"></i>
                    Reset
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reset Filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Button>

          <Button
            className="text-sm  text-black flex items-center gap-1  lg:mb-0 h-8 px-2"
            onClick={() => {
              setOpen(true);
              setAction("add");
              setData(undefined);
            }}
          >
            Open gate
          </Button>
          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>
      <div className="rounded-none border border-border ">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            {/* <Table className="w-full overflow-x-scroll"> */}
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className={
                      checkInLogs !== "read"
                        ? "cursor-pointer hover:bg-secondary"
                        : ""
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={`${cell.row.original.is_manual && cell.column.id == "is_manual" && "bg-red-100 border-l-2 border-red-400"}`}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : manualCheckTable.length > 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No Check-ins found.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No Check-ins found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* pagination */}
      {manualCheckTable.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
        onReset={() => setSelectedUserType(undefined)} // Add this prop
      />

      <ManualCheckInForm
        isOpen={isOpen}
        setOpen={setOpen}
        action={action}
        setAction={setAction}
        data={data}
        setData={setData}
        refetch={refetch}
      />
    </div>
  );
}
